import { Fragment, useMemo } from 'react';
import type Fuse from 'fuse.js';
import type { AutocompleteProps } from '$components/basic/Autocomplete';
import { Autocomplete, AutocompleteInjectName } from '$components/basic/Autocomplete';
import { InjectComponent } from '$util/InjectComponent';

const sliceChunks = (
  str: string,
  indices: ReadonlyArray<Fuse.RangeTuple>
): ReadonlyArray<[k: string, chunk: string]> => {
  let i = 0;
  const chunks: Map<string, string> = new Map();
  indices.forEach(([start, stop]) => {
    if (start > i) chunks.set(`${i}-${start}`, str.slice(i, start));
    chunks.set(`${start}-${stop}-b`, str.slice(start, stop + 1));
    i = stop + 1;
  });
  if (i < str.length) chunks.set(`${i}-${str.length}`, str.slice(i, str.length));
  return Array.from(chunks);
};

export interface SuggestionMatchBase {
  matches: ReadonlyArray<Fuse.FuseResultMatch>;
}

export interface SuggestionMatchProps<TSuggestion>
  extends Pick<AutocompleteProps<TSuggestion>, 'getSuggestionValue'> {
  readonly value: string;
  readonly suggestion: TSuggestion;
}

export function SuggestionMatch<TSuggestion extends SuggestionMatchBase>({
  value,
  suggestion,
  getSuggestionValue,
}: SuggestionMatchProps<TSuggestion>): JSX.Element {
  const name = getSuggestionValue(suggestion);

  const suggestionMatch = useMemo(() => {
    const indices = suggestion.matches?.[0]?.indices || [];
    const chunks = sliceChunks(name, indices);
    const words = value.split(' ').filter((w) => !!w);
    return chunks.map(([k, chunk]) =>
      // highlight only values that exactly match a typed word
      k.endsWith('-b') && words.some((w) => chunk.toLowerCase().includes(w.toLowerCase())) ? (
        <b key={k}>{chunk}</b>
      ) : (
        <Fragment key={k}>{chunk}</Fragment>
      )
    );
  }, [name, suggestion.matches, value]);

  return (
    <InjectComponent name={AutocompleteInjectName.SUGGESTION_MATCH} namespace={Autocomplete.displayName}>
      <div className="SuggestionMatch suggestion">{suggestionMatch}</div>
    </InjectComponent>
  );
}
SuggestionMatch.displayName = 'SuggestionMatch';
